import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart } from "recharts";
import { getCdpInsights } from "../../api";
import { fmtLocation } from "../../utils/format";
import InfoTip from "../shared/InfoTip";
import PanelTakeaway from "../shared/PanelTakeaway";

function relativeTime(ts) { if (!ts) return ""; const s = (Date.now() - new Date(ts).getTime()) / 1000; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }
function stripDomain(url) { if (!url) return ""; try { return new URL(url).pathname; } catch { return url; } }
function truncate(s, m = 30) { return !s || s.length <= m ? s || "" : s.slice(0, m) + "\u2026"; }

const CH_PILL = {
  "Paid Search": "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "Paid Social": "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  "Organic Search": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Direct": "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};
const DEFAULT_PILL = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";

export default function CdpInsightsTab({ clientId, rangeStart, rangeEnd, includeBots = false, isDarkMode, say }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId || !rangeStart || !rangeEnd) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await getCdpInsights(clientId, rangeStart, rangeEnd, { includeBots });
        if (!cancelled) setData(resp?.data || null);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, rangeStart, rangeEnd, includeBots]);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading insights...</div>;
  if (!data) return <div className="text-center py-12 text-gray-400 text-sm">No data available</div>;

  // Defensive defaults (re-tier made this always-mount): a missing/null sub-key
  // must never crash the page — fall back to empty shapes so .slice/.length/.some
  // no-op and the panel shows its clean empty state instead of throwing.
  const { decision_cycle, ghost_demand, returning_analysis, rvv = [] } = data;
  const dc = decision_cycle ?? { by_channel: [] };
  const ghost = ghost_demand ?? { total: 0, visitors: [] };
  const ret = returning_analysis ?? { data: [] };

  return (
    <div className="space-y-6">

      {/* ═══ 9B: GHOST DEMAND ═══ (most valuable panel — leads the Guest Story
          tier; per spec it sits right after Funnel + Segments, not buried.) */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <PanelTakeaway panelKey="ghost_demand" say={say} />
        <h3 className="gi-panel-title text-gray-900 dark:text-white mb-1">Ghost Demand <InfoTip text="Visitors who browsed extensively with high engagement but never clicked Book Now — demand you're not capturing. The most valuable recovery targets." /></h3>
        {ghost.total > 0 && (
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg px-4 py-3 mb-4">
            <span className="text-xs text-orange-700 dark:text-orange-300">
              {ghost.total} high-intent visitor{ghost.total !== 1 ? "s" : ""} browsed your site extensively but never clicked Book Now.
              They spent an average of {ghost.avg_minutes} minutes across {ghost.avg_sessions} sessions.
            </span>
          </div>
        )}

        <div className="space-y-2">
          {ghost.visitors.slice(0, 20).map((g, i) => (
            <div key={g.cv_id || i} className="border-l-4 border-orange-400 bg-orange-50/50 dark:bg-orange-900/10 rounded-r-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                  {"\ud83d\udfe0"} {g.city ? `Visitor from ${fmtLocation(g.city, g.region, g.country)}` : "Anonymous visitor"}
                </span>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">{relativeTime(g.last_seen)}</span>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {g.sessions} session{g.sessions !== 1 ? "s" : ""} &middot; {g.pages} pages &middot; ~{Math.round(g.heartbeats * 30 / 60)}m active
              </div>
              <div className="flex items-center gap-2 mt-1">
                {g.watched_video && <span className="text-[10px] text-gray-500">{"\ud83d\udcfa"} Watched video</span>}
                {g.hovered_booking && <span className="text-[10px] text-gray-500">{"\ud83d\udd0d"} Hovered booking</span>}
                {g.clicked_phone && <span className="text-[10px] text-gray-500">{"\ud83d\udcde"} Phone click</span>}
                {g.channel && <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${CH_PILL[g.channel] || DEFAULT_PILL}`}>{g.channel}</span>}
              </div>
              {g.last_page && <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Last page: {truncate(stripDomain(g.last_page))}</div>}
            </div>
          ))}
          {ghost.total === 0 && <div className="text-center py-6 text-gray-400 text-sm">No ghost demand detected this period</div>}
        </div>
      </div>

      {/* ═══ 9A: DECISION CYCLE LENGTH ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <PanelTakeaway panelKey="decision_cycle" say={say} />
        <h3 className="gi-panel-title text-gray-900 dark:text-white mb-4">Decision Cycle Length <InfoTip text="How long it takes visitors to decide to book — measured from first visit to booking completion. Includes average days, sessions, and the share who book on their first visit." /></h3>

        {/* 2026-08-14 (Epic 5) — empty state when there are no bookings with
            decision-cycle data. Was showing "0 days / 0 sessions / 0%" which
            read as a finding when it means "no data yet." */}
        {(!dc?.summary || (dc.summary.avg_days_to_book === 0 && dc.summary.avg_sessions_to_book === 0)) ? (
          <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
            No bookings with decision-cycle data in this period.
          </div>
        ) : (
        <>
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900 dark:text-white">{dc.summary.avg_days_to_book} days</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">Avg time to book</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900 dark:text-white">{dc.summary.avg_sessions_to_book}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">Avg sessions before booking</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900 dark:text-white">{dc.summary.pct_first_visit}%</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">Book on first visit</div>
          </div>
        </div>

        {/* By channel */}
        {dc.by_channel.length > 0 && (
          <div className="overflow-x-auto responsive-card-table-wrapper">
          <table className="w-full text-sm responsive-card-table">
            <thead><tr className="border-b border-gray-200 dark:border-gray-800">
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">First Channel</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Converters</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Avg Days</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Avg Sessions</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">1st Visit %</th>
            </tr></thead>
            <tbody>{dc.by_channel.map((c, i) => (
              <tr key={c.first_channel} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""}`}>
                <td className="px-3 py-2" data-label="First Channel"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${CH_PILL[c.first_channel] || DEFAULT_PILL}`}>{c.first_channel}</span></td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300" data-label="Converters">{c.converters}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300" data-label="Avg Days">{c.avg_days}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300" data-label="Avg Sessions">{c.avg_sessions}</td>
                <td className="px-3 py-2 text-right text-xs text-gray-600 dark:text-gray-400" data-label="1st Visit %">{c.pct_first_visit}%</td>
              </tr>
            ))}</tbody>
          </table>
          </div>
        )}
        </>
        )}
      </div>

      {/* ═══ 9C: RETURNING VISITOR ANALYSIS ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <PanelTakeaway panelKey="returning" say={say} />
        <h3 className="gi-panel-title text-gray-900 dark:text-white mb-1">Returning Visitor Analysis <InfoTip text="Visitors who came back at least once after their first visit. Shows return rate, average time between visits, and how many sessions before they act." /></h3>
        {ret.multiplier > 1 && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Visitors who come back 3+ times are {ret.multiplier}x more likely to book than first-time visitors.
          </p>
        )}
        {ret.data.length > 0 ? (
          <div className="max-[499px]:-mx-3">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={ret.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#E5E7EB"} />
              <XAxis dataKey="frequency" tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} unit="%" />
              <Tooltip contentStyle={{ backgroundColor: isDarkMode ? "#1F2937" : "#fff", border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`, borderRadius: 8, fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="visitors" fill="#6B7280" radius={[4, 4, 0, 0]} barSize={40} name="Visitors" />
              <Line yAxisId="right" type="monotone" dataKey="booking_rate" stroke="#059669" strokeWidth={2} dot={{ fill: "#059669", r: 4 }} name="Booking Rate %" />
            </ComposedChart>
          </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400 text-sm">No returning visitor data</div>
        )}
      </div>

      {/* ═══ 9D: RETURN VISITOR VELOCITY ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <h3 className="gi-panel-title text-gray-900 dark:text-white mb-1">Return Visitor Velocity <InfoTip text="Rate at which visitors return over time — how quickly they come back after their first visit. A rising velocity suggests growing interest." /></h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">How quickly do return visitors come back? Faster return = higher intent.</p>
        {rvv.some(r => r.visitors > 0) ? (
          <div className="max-[499px]:-mx-3">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={rvv} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#E5E7EB"} />
              <XAxis dataKey="return_speed" tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
              <YAxis tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
              <Tooltip contentStyle={{ backgroundColor: isDarkMode ? "#1F2937" : "#fff", border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`, borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="visitors" fill="#6366F1" radius={[4, 4, 0, 0]} barSize={50} name="Visitors" />
            </BarChart>
          </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400 text-sm">No return velocity data</div>
        )}
      </div>
    </div>
  );
}
